import { useState } from "react";
import { motion } from "framer-motion";
import { Play, Brain, User, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CARD_CATEGORIES } from "@/utils/memoryGameData";
import { savePlayerNames, loadPlayerNames } from "@/utils/playerStorage";

export const MemoryGameSettings = ({ onStartGame }) => {
  const [category, setCategory] = useState("animals");
  const [pairCount, setPairCount] = useState(6);
  const [playerCount, setPlayerCount] = useState(1);
  const [playerNames, setPlayerNames] = useState(() => loadPlayerNames());

  const PLAYER_COLORS = ["#4ECDC4", "#FF6B6B", "#A855F7", "#EAB308"];

  const categories = Object.entries(CARD_CATEGORIES).map(([key, value]) => ({
    key,
    ...value
  }));

  const pairOptions = [
    { value: 4, label: "4 Pasang (Pemula)" },
    { value: 5, label: "5 Pasang (Mudah)" },
    { value: 6, label: "6 Pasang (Sedang)" },
    { value: 7, label: "7 Pasang (Cukup)" },
    { value: 8, label: "8 Pasang (Sulit)" },
    { value: 9, label: "9 Pasang (Lebih Sulit)" },
    { value: 10, label: "10 Pasang (Sangat Sulit)" },
    { value: 11, label: "11 Pasang (Tantangan)" },
    { value: 12, label: "12 Pasang (Ekstrem)" },
    { value: 14, label: "14 Pasang (7x4 Hebat)" },
    { value: 15, label: "15 Pasang (6x5 Pro)" },
    { value: 16, label: "16 Pasang (8x4 Jago)" },
    { value: 18, label: "18 Pasang (6x6 Master)" },
    { value: 20, label: "20 Pasang (8x5 Legend)" },
  ];

  const selectedCat = CARD_CATEGORIES[category];

  const handleStart = () => {
    savePlayerNames(playerNames);
    const names = playerNames.slice(0, playerCount).map((n, i) => n || `Pemain ${i + 1}`);
    onStartGame(category, pairCount, playerCount > 1, names[0], names[1] || "Pemain 2", playerCount, names);
  };

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="glass-card-strong rounded-2xl p-4 sm:p-6 w-full max-w-2xl mx-auto"
      data-testid="memory-game-settings"
    >
      <div className="flex items-center justify-center gap-2 mb-3">
        <Brain className="w-7 h-7 text-cyan-300" />
        <h2 className="font-fredoka text-xl sm:text-2xl text-gold font-bold">Memory Game</h2>
      </div>

      <p className="font-fredoka text-gold-light/80 text-sm text-center mb-4">
        Cocokkan 2 kartu yang sama! Latih ingatan sambil belajar.
      </p>

      {/* Player Mode */}
      <div className="mb-4">
        <label className="font-fredoka text-gold-light text-sm mb-2 block font-bold">Mode Pemain</label>
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3, 4].map(n => (
            <Button key={n}
              data-testid={`memory-mode-${n}p`}
              variant={playerCount === n ? "default" : "outline"}
              onClick={() => setPlayerCount(n)}
              className={`font-fredoka text-xs font-bold rounded-xl ${playerCount === n
                ? "bg-gradient-to-r from-cyan-400 to-teal-500 text-white border-0 shadow-lg"
                : "glass-card border-gold/20 text-gold hover:bg-gold/10"
              }`}>
              {n === 1 ? <><User className="w-3 h-3 mr-1" />Solo</> : <><Users className="w-3 h-3 mr-1" />{n}P</>}
            </Button>
          ))}
        </div>
      </div>

      {/* Player Names */}
      {playerCount > 1 && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="mb-4">
          <div className="grid grid-cols-2 gap-2">
            {Array.from({ length: playerCount }).map((_, i) => (
              <div key={i}>
                <label className="font-fredoka text-xs mb-1 block font-bold" style={{ color: PLAYER_COLORS[i] }}>Pemain {i + 1}</label>
                <Input data-testid={`memory-player${i + 1}-name`}
                  value={playerNames[i]} onChange={(e) => { const n = [...playerNames]; n[i] = e.target.value; setPlayerNames(n); }}
                  placeholder={`Pemain ${i + 1}`}
                  className="glass-card border-gold/20 text-white placeholder:text-gold-light/40 font-fredoka rounded-xl" maxLength={15} />
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Category Selection */}
      <div className="mb-4">
        <label className="font-fredoka text-gold-light text-sm mb-2 block font-bold">
          Pilih Materi Belajar ({categories.length} Kategori)
        </label>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[200px] overflow-y-auto pr-1 custom-scrollbar">
          {categories.map((cat, index) => (
            <motion.div
              key={cat.key}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.02 }}
            >
              <Button
                data-testid={`memory-category-${cat.key}`}
                variant={category === cat.key ? "default" : "outline"}
                onClick={() => setCategory(cat.key)}
                className={`game-card flex flex-col items-center py-2 px-1 h-auto w-full rounded-xl ${
                  category === cat.key
                    ? "text-gold-light hover:opacity-90 shadow-lg border-0"
                    : "glass-card border-gold/20 text-gold hover:bg-gold/10 hover:border-gold/30"
                }`}
                style={category === cat.key ? {
                  background: `linear-gradient(135deg, ${cat.color || '#FFD700'}, ${cat.color || '#FFD700'}cc)`,
                  boxShadow: `0 0 16px ${cat.color || '#FFD700'}50`,
                } : {}}
              >
                <span className="text-lg mb-0.5">{cat.icon}</span>
                <span className="text-[9px] sm:text-[11px] font-fredoka leading-tight font-bold">{cat.name}</span>
              </Button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Difficulty Selection */}
      <div className="mb-4">
        <label className="font-fredoka text-gold-light text-sm mb-2 block font-bold">Tingkat Kesulitan</label>
        <Select value={pairCount.toString()} onValueChange={(v) => setPairCount(parseInt(v))}>
          <SelectTrigger className="glass-card border-gold/20 text-white font-fredoka rounded-xl" data-testid="memory-pair-count-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="glass-card-strong border-gold/20 rounded-xl">
            {pairOptions.map((option) => (
              <SelectItem key={option.value} value={option.value.toString()} className="text-white font-fredoka">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Preview */}
      <motion.div
        key={category}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4 p-3 rounded-xl glass-card overflow-hidden relative"
      >
        <p className="font-fredoka text-gold-light/80 text-xs text-center mb-2">
          Preview: <span style={{ color: selectedCat?.color }} className="font-bold">{selectedCat?.name}</span> dengan {pairCount} pasang kartu
          {playerCount > 1 && <span className="text-pink-300 font-bold"> ({playerCount} Pemain)</span>}
        </p>
        <div className="flex justify-center gap-2">
          {selectedCat?.cards.slice(0, 5).map((card, i) => (
            <motion.span key={i} className="text-2xl" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.06, type: "spring" }}>
              {card.display}
            </motion.span>
          ))}
          {selectedCat?.cards.length > 5 && <span className="text-gold-light/50 self-center text-sm">...</span>}
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: selectedCat?.color || '#FFD700' }} />
      </motion.div>

      {/* Start Button */}
      <Button
        onClick={handleStart}
        className="w-full font-fredoka font-bold uppercase tracking-wide text-white rounded-xl h-12 text-base shadow-lg"
        style={{ background: `linear-gradient(135deg, ${selectedCat?.color || '#FFD700'}, ${selectedCat?.color || '#FFD700'}cc)`, boxShadow: `0 0 20px ${selectedCat?.color || '#FFD700'}50` }}
        data-testid="memory-start-btn"
      >
        <Play className="w-5 h-5 mr-2" />
        {playerCount > 1 ? `Mulai Bermain Ber-${playerCount}!` : 'Mulai Bermain!'}
      </Button>
    </motion.div>
  );
};

export default MemoryGameSettings;
